<template>
  <div v-if="isHydrating" class="flex-col flex items-center justify-center h-screen">
    <Loader />
  </div>
  <router-view v-else />
</template>

<script lang="ts">
import { Loader } from "@toruslabs/vue-components/Loader";
import { defineComponent } from "vue";

import { orgAnalytics, projectAnalytics } from "@/helpers/analytics";
import { captureAttribution, toInitialTouchProperties, toLatestTouchProperties } from "@/helpers/attribution";
import { syncStoredAttribution } from "@/helpers/attributionPersistence";
import { clearSocialSpaSession } from "@/helpers/auth/spaClient";
import log from "@/helpers/log";
import { clearAuthStorage } from "@/store/modules/authStorage";
import { setAccessToken } from "@/utils/accessToken";
import { ROUTES } from "@/utils/enums";

import { teamModule, userModule } from "./store";

export default defineComponent({
  name: "App",
  components: { Loader },
  data: () => ({
    isHydrating: false,
  }),
  async created() {
    try {
      this.isHydrating = true;
      // Capture before anything can redirect or clear the URL. rehydrate()
      // returns early for agentic/invite/login-email routes, so capturing
      // afterwards would silently lose UTM tags on those landings. Each
      // authenticated flow persists the parked touch once an access token exists.
      captureAttribution();
      await this.rehydrate();
    } catch (error) {
      log.error("error while rehydrating", error);
    } finally {
      this.isHydrating = false;
    }
  },
  methods: {
    async clearStaleAuthSession(): Promise<void> {
      clearAuthStorage();
      setAccessToken(null);
      userModule.setUserInfo(undefined);
      await clearSocialSpaSession();
    },
    async rehydrate() {
      const route = this.$router.resolve({ path: window.location.pathname, query: Object.fromEntries(new URLSearchParams(window.location.search)) });
      const { query = {} } = route;

      // let agentic handle its own rehydration
      const isAgenticPath = route.path.startsWith("/agentic");
      if (isAgenticPath) {
        return;
      }

      // skip rehydration for invitation and accept-invite routes
      if (
        [ROUTES.INVITATION, ROUTES.ACCEPT_INVITE, ROUTES.START, ROUTES.END, ROUTES.AGENTIC_APPROVAL, ROUTES.LOGIN_EMAIL].includes(
          route.name as string
        )
      ) {
        return;
      }

      if (!(await userModule.rehydrateSession())) {
        this.$router.replace({ name: "login", query });
        return;
      }

      try {
        if (!(await userModule.authenticateWithDevDashboard())) {
          this.$router.replace({ name: "create-profile", query });
          return;
        }
      } catch (error) {
        const err = error as { code?: number };
        if (err.code === 401) {
          await this.clearStaleAuthSession();
          this.$router.replace({ name: "login", query });
          return;
        }
        throw error;
      }

      await userModule.getUserProfile();

      // Now that a token exists, push any attribution parked before login.
      void syncStoredAttribution();

      // analytics
      const { authConnection } = userModule.userInfoGetter || {};
      // First touch comes from the user row so it resolves on any device;
      // latest touch describes this session. Both ride on every event, frozen
      // at fire time, so historical reports stay reproducible.
      const eventAttribution = {
        ...toInitialTouchProperties(userModule.userProfileGetter.attribution),
        ...toLatestTouchProperties(),
      };
      orgAnalytics.setGlobalProperties({
        user_id: userModule.userProfileGetter.ID,
        user_auth_connection: authConnection,
        user_type_of_login: userModule.typeOfLogin,
        user_mfa_enabled: false,
        ...eventAttribution,
      });
      projectAnalytics.setGlobalProperties({
        user_id: userModule.userProfileGetter.ID,
        user_auth_connection: authConnection,
        user_type_of_login: userModule.typeOfLogin,
        user_mfa_enabled: false,
        ...eventAttribution,
      });

      if (!userModule.userProfileGetter.accepted_terms_and_privacy_policy) {
        this.$router.push({ name: "update-profile", query });
        return;
      }

      if (query.invite_code) {
        this.$router.replace({ name: "accept-invite", query });
        return;
      }

      await teamModule.getTeams();

      if (teamModule.teamsGetter.length === 0) {
        this.$router.replace({ name: "create-organization" });
        return;
      }

      if (route.path === "/" || route.name === "login") {
        this.$router.replace({ name: "home" });
      }
    },
  },
});
</script>
